<template>
  <div class="rsystem-setup">
    <div class="row">
      <div class="col-md-6 form-group">
        <label>{{ $t('rsystem.apiKey') }}</label>
        <input v-model="draft.api_key" type="password" autocomplete="new-password" class="form-control" @input="credentialsChanged">
      </div>
      <div class="col-md-6 form-group">
        <label>{{ $t('rsystem.settings') }}</label><br>
        <button type="button" class="btn add" :disabled="busy || !draft.api_key" @click="loadOrganizations">{{ $t('rsystem.fetchOrganizations') }}</button>
      </div>
    </div>
    <p v-if="error" role="alert" class="text-danger">{{ error }}</p>
    <div v-if="organizations.length" class="form-group">
      <label>{{ $t('rsystem.organisationId') }}</label>
      <el-select v-model="draft.organization_id" filterable :disabled="busy" :placeholder="$t('rsystem.choose')" @change="organizationChanged">
        <el-option v-for="item in organizations" :key="item.id" :value="item.id" :label="item.name || item.id" class="rsystem-option">
          <span class="option-title">{{ item.name || item.id }}</span>
          <span class="option-subtitle" :title="item.id">{{ item.id }}</span>
          <span v-if="item.address" class="option-subtitle">{{ item.address }}</span>
        </el-option>
      </el-select>
      <small v-if="draft.organization_id" class="selection-id">{{ draft.organization_id }}</small>
    </div>
    <template v-if="options">
      <div class="row">
        <div v-for="field in entityFields" :key="field.key" class="col-md-6 form-group">
          <label>{{ $t('rsystem.' + field.label) }}</label>
          <el-select v-model="draft[field.key]" clearable filterable :disabled="busy" :placeholder="$t('rsystem.choose')" @change="entityChanged(field.key)">
            <el-option v-for="item in options[field.list]" :key="item.id" :value="item.id" :label="item.name || item.id" class="rsystem-option">
              <span class="option-title">{{ item.name || item.id }}</span>
              <span class="option-subtitle" :title="item.id">{{ item.id }}</span>
              <span v-if="item.mode" class="option-detail">{{ discountLabel(item) }}</span>
            </el-option>
          </el-select>
          <small v-if="draft[field.key]" class="selection-id">{{ draft[field.key] }}</small>
          <small v-if="selectedEntity(field) && selectedEntity(field).mode" class="selection-detail">{{ discountLabel(selectedEntity(field)) }}</small>
        </div>
      </div>
      <label>{{ $t('rsystem.paymentTypes') }}</label>
      <div v-for="type in paymentTypes" :key="type" class="row form-group">
        <label class="col-md-3">{{ $t('rsystem.payment_' + type) }}</label>
        <div class="col-md-9">
          <el-select v-model="paymentIds[type]" clearable filterable :disabled="busy" :placeholder="$t('rsystem.choose')">
            <el-option v-for="item in paymentChoices" :key="item.id" :value="item.id" :label="item.name || item.id" class="rsystem-option">
              <span class="option-title">{{ item.name || item.id }}</span>
              <span class="option-subtitle" :title="item.id">{{ item.id }}</span>
              <span class="option-detail">{{ item.paymentTypeKind }}</span>
            </el-option>
          </el-select>
          <small v-if="paymentIds[type]" class="selection-id">{{ paymentIds[type] }}</small>
        </div>
      </div>
      <p class="help-block">{{ $t('rsystem.reviewMappings') }}</p>
      <p class="help-block"><strong>{{ $t('rsystem.destination') }}:</strong> {{ selectedOrganizationLabel }} / {{ selectedTerminalLabel }}</p>
    </template>
    <p v-if="notice" role="status" class="help-block">{{ notice }}</p>
    <p v-if="menuRefreshRequired" class="text-warning">{{ $t('rsystem.refreshMenu') }}</p>
    <div v-if="config.id" class="form-group mar-to-15">
      <button type="button" class="btn add" :disabled="busy || dirty || !options" @click="importMenu">{{ $t('rsystem.import') }}</button>
      <p class="help-block">{{ $t('rsystem.deleteMenu') }}. {{ $t('rsystem.seoText') }}</p>
    </div>
  </div>
</template>
<script>
import { Select, Option } from 'element-ui'
import swal from 'sweetalert2'

export default {
  components: { [Select.name]: Select, [Option.name]: Option },
  props: { location: { type: Object, required: true } },
  data () {
    return {
      draft: {},
      organizations: [],
      options: null,
      paymentIds: {},
      busy: false,
      error: '',
      notice: '',
      requestVersion: 0,
      savedSnapshot: '',
      menuRefreshRequired: false,
      paymentTypes: ['CASH', 'CARD', 'POS', 'CARD_POS', 'ONLINE'],
      entityFields: [
        { key: 'terminal_group_id', list: 'terminal_groups', label: 'terminalGroupId' },
        { key: 'tips_pos_id', list: 'tips_types', label: 'tipsId' },
        { key: 'discount_pos_id', list: 'discounts', label: 'discountId' }
      ]
    }
  },
  computed: {
    config () { return this.location.r_system_company || {} },
    paymentChoices () {
      return this.options ? this.options.payment_methods.filter(item => item.id && item.paymentTypeKind &&
        (!this.draft.terminal_group_id || !item.terminalGroups || !item.terminalGroups.length || item.terminalGroups.some(group => group.id === this.draft.terminal_group_id))) : []
    },
    canSave () { return this.draft.organization_id && this.draft.terminal_group_id && Object.keys(this.paymentIds).some(type => this.paymentIds[type]) },
    dirty () { return this.snapshot() !== this.savedSnapshot },
    selectedOrganizationLabel () { const item = this.organizations.find(item => item.id === this.draft.organization_id); return item ? item.name || item.id : '' },
    selectedTerminalLabel () { const item = this.options && this.options.terminal_groups.find(item => item.id === this.draft.terminal_group_id); return item ? item.name || item.id : '' }
  },
  watch: {
    busy (value) { this.$emit('busy', value) },
    'location.true_id': { immediate: true, handler () { this.initialize() } }
  },
  beforeDestroy () { this.requestVersion++; this.$emit('busy', false) },
  methods: {
    selectedEntity (field) { return this.options && this.options[field.list].find(item => item.id === this.draft[field.key]) },
    discountLabel (item) {
      const mode = { Percent: 'proc', FixedSum: 'fix', FlexibleSum: 'adjustable' }[item.mode] || item.mode
      const value = item.mode === 'Percent' ? ' (' + item.percent + '%)' : item.mode === 'FixedSum' ? ' (' + item.sum + ')' : ''
      return this.$t('rsystem.' + mode) + value + (item.recommended ? ' · ' + this.$t('rsystem.recommended') : '')
    },
    normalizePayments (value) {
      if (typeof value === 'string') {
        try { value = JSON.parse(value) } catch (e) { return {} }
      }
      if (Array.isArray(value)) return Object.assign({}, ...value)
      return value || {}
    },
    initialize () {
      this.requestVersion++
      this.draft = Object.assign({ api_key: '', organization_id: null, terminal_group_id: null, discount_pos_id: null, tips_pos_id: null }, this.config)
      this.paymentIds = {}
      const mappings = this.normalizePayments(this.config.payment_types)
      this.paymentTypes.forEach(type => {
        this.$set(this.paymentIds, type, mappings[type] && Object.values(mappings[type])[0] || null)
      })
      this.organizations = []
      this.options = null
      this.error = ''
      this.notice = ''
      this.menuRefreshRequired = false
      this.busy = false
      this.savedSnapshot = this.snapshot()
      if (this.location.true_id && this.draft.api_key) this.loadOrganizations()
    },
    snapshot () {
      return JSON.stringify([this.draft.api_key, this.draft.organization_id, this.draft.terminal_group_id,
        this.draft.discount_pos_id, this.draft.tips_pos_id, this.paymentTypes.map(type => this.paymentIds[type] || null)])
    },
    credentialsChanged () {
      this.requestVersion++
      this.busy = false
      this.organizations = []
      this.clearDependent()
      this.draft.organization_id = null
      this.error = ''
      this.notice = ''
    },
    clearDependent () {
      this.options = null
      this.draft.terminal_group_id = null
      this.draft.discount_pos_id = null
      this.draft.tips_pos_id = null
      this.paymentIds = {}
    },
    request (action, extra, callback) {
      const version = ++this.requestVersion
      const locationId = this.location.true_id
      this.busy = true
      this.error = ''
      const body = Object.assign({ token: this.$session.get('token'),
        company_id: this.location.company_id,
        location_id: locationId,
        location_token: this.location.id,
        api_key: this.draft.api_key,
        organization_id: this.draft.organization_id }, extra)
      return new Promise(resolve => {
        this.apiRequest('/rsystems/' + (this.config.id || 0) + '/' + action, 'POST', body, response => {
          if (version !== this.requestVersion || locationId !== this.location.true_id) {
            resolve({ status: false, canceled: true })
            return
          }
          this.busy = false
          if (!response.status) {
            this.error = response.errors && response.errors.message || this.$t('rsystem.requestFailed')
          } else if (callback) {
            callback(response)
          }
          resolve(response)
        })
      })
    },
    loadOrganizations () {
      this.request('organizations', {}, response => {
        this.organizations = response.data.organizations
        if (!this.organizations.some(item => item.id === this.draft.organization_id)) {
          this.draft.organization_id = null
          this.clearDependent()
        }
        // Even a single organization is explicitly chosen by the user for new setup.
        if (this.draft.organization_id) this.loadOptions()
      })
    },
    entityChanged (key) {
      if (key !== 'terminal_group_id') return
      this.paymentTypes.forEach(type => {
        if (!this.paymentChoices.some(item => item.id === this.paymentIds[type])) this.$set(this.paymentIds, type, null)
      })
    },
    organizationChanged () {
      this.clearDependent()
      this.notice = ''
      this.menuRefreshRequired = this.draft.organization_id !== this.config.organization_id
      this.loadOptions()
    },
    loadOptions () {
      this.request('setup-options', {}, response => {
        this.options = response.data
        this.entityFields.forEach(field => {
          const choices = this.options[field.list]
          if (!choices.some(item => item.id === this.draft[field.key])) {
            this.draft[field.key] = choices.length === 1 ? choices[0].id : null
          }
        })
        this.paymentTypes.forEach(type => {
          const oldId = this.paymentIds[type]
          const suggestion = this.options.suggested_payment_types[type]
          this.$set(this.paymentIds, type, this.paymentChoices.some(item => item.id === oldId)
            ? oldId : suggestion ? Object.values(suggestion)[0] : null)
        })
      })
    },
    saveIfDirty () {
      if (!this.dirty) return Promise.resolve(true)
      if (this.busy || !this.options || !this.canSave || !this.location.true_id) {
        this.error = this.$t('rsystem.completeSetup')
        return Promise.resolve(false)
      }
      return this.save().then(response => !!response.status)
    },
    save () {
      const mappings = {}
      this.paymentTypes.forEach(type => {
        const payment = this.paymentChoices.find(item => item.id === this.paymentIds[type])
        if (payment) mappings[type] = { [payment.paymentTypeKind]: payment.id }
      })
      return this.request('setup', { terminal_group_id: this.draft.terminal_group_id,
        discount_pos_id: this.draft.discount_pos_id,
        tips_pos_id: this.draft.tips_pos_id,
        payment_types: mappings,
        updated_at: this.config.updated_at }, response => {
          this.$emit('saved', response.data)
          this.savedSnapshot = this.snapshot()
          this.menuRefreshRequired = this.menuRefreshRequired || response.menu_refresh_required
          this.notice = this.$t('rsystem.saved')
        })
    },
    importMenu () {
      swal({ text: this.$t('rsystem.importConfirm'), type: 'warning', showCancelButton: true }).then(confirmed => {
        if (!confirmed || this.dirty) return
        this.request('import-products', {}, () => {
          this.menuRefreshRequired = false
          this.notice = this.$t('rsystem.importQueued')
        })
      }).catch(() => {})
    }
  }
}
</script>
<style scoped>
.el-select { width: 100%; }
.rsystem-setup { padding-top: 12px; }
.rsystem-option { height: auto; min-height: 56px; padding-top: 8px; padding-bottom: 8px; line-height: 1.5; }
.option-title { display: block; font-weight: 600; }
.option-subtitle, .selection-id { display: block; color: #707b8f; font-size: 11px; line-height: 1.5; font-weight: 400; }
.option-subtitle { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.selection-id { margin-top: 5px; overflow-wrap: anywhere; }
.option-detail, .selection-detail { display: block; color: #42566f; font-size: 12px; line-height: 1.5; }
.selection-detail { margin-top: 3px; }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Settings/Locations/RSystemSetup.vue